"use client";

import { useState } from "react";
import { RxCross2 } from "react-icons/rx";
import Image from "next/image";
import InputField from "../ui/InputField";
import Button from "../ui/buttons";
import { usePublicAuth } from "@/lib/auth/PublicAuthContext";

export default function LoginRegister({
  onClose,
  onContinue,
}: {
  onClose?: () => void;
  onContinue?: () => void;
}) {
  const { sendOTP, isLoading, error, clearError, setAuthEmail } = usePublicAuth();
  const [email, setEmail] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleContinue = async () => {
    setLocalError(null);
    clearError();

    if (!email.trim()) {
      setLocalError("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setLocalError("Please enter a valid email address");
      return;
    }

    const result = await sendOTP(email, "login");

    if (result.success) {
      setAuthEmail(email);
      localStorage.setItem("auth_email", email);
      onContinue?.();
    } else {
      setLocalError(result.message || "Failed to send OTP");
    }
  };

  const displayError = localError || error;

  return (
    <div className="bg-F8F8F8 w-full h-full overflow-x-hidden p-[10px] sm:p-6">
      <div className="flex flex-col gap-6">
        {/* Header Section */}
        <div className="relative">
          {/* Close Button */}
          <button
            onClick={() => (onClose ? onClose() : window.history.back())}
            className="absolute right-0 top-0 text-808080-600 hover:text-000000 transition-all duration-200 transform hover:scale-110 cursor-pointer"
            aria-label="Close"
          >
            <RxCross2 size={22} />
          </button>
        </div>

        {/* Logo */}
        <div className="flex justify-center">
          <Image
            src="https://media.dinamani.com/dinamani/2026-02-27/y9s7ztw3/dinamanilogo.png"
            alt="Dinamani Logo"
            width={100}
            height={43}
            className="object-contain"
          />
        </div>

        {/* Tamil subtitle */}
        <p className="text-center text-4D4D4D text-14-tamil-600">
          தினமணியுடன் பதிப்பித்த நிலையில் இருப்புகள்
        </p>

        {/* Divider */}
        <div className="-mx-[10px] sm:-mx-6 border-b border-DCDCDC"></div>

        <div className="flex flex-col gap-4">
          {/* Login Title */}
          <h2 className="mt-4 text-center text-18-tamil-700 text-1B1B1B">Login or Register</h2>

          {/* Error Message */}
          {displayError && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-14-tamil-400 text-center rounded">
              {displayError}
            </div>
          )}

          {/* Input */}
          <InputField
            placeholder="Email Address"
            value={email}
            onChange={(val) => {
              setEmail(val);
              if (localError) setLocalError(null);
              if (error) clearError();
            }}
            height="48px"
          />

          {/* Continue Button */}
          <Button
            variant="primary"
            onClick={handleContinue}
            disabled={isLoading}
            className="w-full text-18-tamil-700 text-FFFFFF cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Sending OTP..." : "Continue"}
          </Button>
        </div>

        {/* OR separator */}
        {/* <div className="my-3">
          <div className="flex items-center justify-center">
            <span className="text-1B1B1B text-12-tamil-400 px-4 bg-F8F8F8 z-10">
              அல்லது
            </span>
          </div>
          <div className="border-b border-DCDCDC -mt-3"></div>
        </div> */}

        {/* <div className="flex flex-col gap-4"> */}
        {/* Google Button */}
        {/* <button className="w-full text-1B1B1B bg-FFFFFF border border-D2D2D2 px-4 py-2 flex items-center justify-center gap-3 text-14-tamil-400 h-[40px]">
            <Image src="/dinamani/google.png" alt="Google" width={18} height={18} loading="lazy" />
            Google உடன் தொடரவும்
          </button> */}

        {/* Facebook Button */}
        {/* <button className="w-full text-1B1B1B border border-D2D2D2 px-4 py-2 flex items-center justify-center gap-3 text-14-tamil-400 h-[40px]">
            <Image src="/dinamani/fb.png" alt="Facebook" width={21} height={20} loading="lazy" />
            Facebook உடன் தொடரவும்
          </button>
        </div> */}

        {/* Footer */}
        <div className="text-center pt-8 text-4D4D4D text-10-tamil-400">
          <div className="flex justify-center gap-2 mb-2">
            <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="hover:underline">Terms of Use</a> |
            <a href="/contact-us" target="_blank" rel="noopener noreferrer" className="hover:underline">Contact Us</a>
          </div>

          <p className="leading-5">
            ©2026 தினமணி மீடியா பிரைவேட் லிமிடெட். அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.
            தகவியின்மை கொள்கை மற்றும் பயன்பாட்டு விதிமுறைகள்.
          </p>
        </div>
      </div>
    </div>
  );
}
